'use client';
import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '../lib/utils';
import { cva } from 'class-variance-authority';
const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;
const closeVariants = cva('absolute rounded-sm opacity-70 ring-black transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:text-black', {
    variants: {
        size: {
            sm: 'right-4 top-4 h-5 w-5',
            lg: 'right-3.5 top-3.5 md:right-[1.375rem] md:top-4 h-8 w-8 md:h-10 md:w-10',
        },
    },
    defaultVariants: {
        size: 'sm',
    },
});
const DialogCloseX = React.forwardRef(({ className, size, ...props }, ref) => (React.createElement(DialogPrimitive.Close, { ref: ref, className: cn(closeVariants({ size }), className), ...props },
    React.createElement(X, { className: "w-full h-full" }),
    React.createElement("span", { className: "sr-only" }, "Close"))));
DialogCloseX.displayName = 'DialogCloseX';
const overlayVariants = cva('fixed inset-0 z-40', {
    variants: {
        variant: {
            default: 'bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            invisible: '',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});
const DialogOverlay = React.forwardRef(({ className, variant, ...props }, ref) => (React.createElement(DialogPrimitive.Overlay, { ref: ref, className: cn(overlayVariants({ variant }), className), ...props })));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
const DialogContent = React.forwardRef(({ className, children, overlay = 'default', closeSize = 'sm', hideClose = false, dismissible = true, ...props }, ref) => (React.createElement(DialogPortal, null,
    React.createElement(DialogOverlay, { className: overlay }),
    React.createElement(DialogPrimitive.Content, { ref: ref, className: cn('fixed left-[50%] top-[50%] z-40 overflow-y-auto grid w-full max-w-[min(912px,95vw)] max-h-screen translate-x-[-50%] translate-y-[-50%] border bg-white shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-[10px]', className), ...props, ...(dismissible
            ? {}
            : {
                onPointerDownOutside: (event) => event.preventDefault(),
                onInteractOutside: (event) => event.preventDefault(),
            }) },
        children,
        !hideClose && dismissible && React.createElement(DialogCloseX, { size: closeSize })))));
DialogContent.displayName = DialogPrimitive.Content.displayName;
const DialogHeader = ({ className, ...props }) => (
//Max width is to prevent the title from overlaping the close button in small screens
React.createElement("div", { className: cn('flex flex-col text-center sm:text-left max-w-[calc(100%-25px)]', className), ...props }));
DialogHeader.displayName = 'DialogHeader';
const DialogFooter = ({ className, ...props }) => (React.createElement("div", { className: cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className), ...props }));
DialogFooter.displayName = 'DialogFooter';
const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (React.createElement(DialogPrimitive.Title, { ref: ref, className: cn('text-lg font-semibold leading-none tracking-tight', className), ...props })));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (React.createElement(DialogPrimitive.Description, { ref: ref, className: cn('text-sm xl:text-base', className), ...props })));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
export { Dialog, DialogPortal, DialogOverlay, DialogClose, DialogCloseX, DialogTrigger, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription, };
